// /Users/stuartmcgeer/portalworx/frontend/src/contexts/PermissionContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axiosInstance from '../services/axiosInstance';

// Define the shape of the permissions object
interface Permissions {
    can_edit_users: string[]; // List of user IDs or roles that can be edited
    can_delete_users: string[]; // List of user IDs or roles that can be deleted
    can_create_users: boolean; // Flag indicating if users can be created
    can_change_user_roles: boolean; // Flag indicating if user roles can be changed
    can_create_projects: boolean; // Flag indicating if projects can be created
    can_edit_projects: boolean; // Flag indicating if projects can be edited
    can_delete_projects: boolean; // Flag indicating if projects can be deleted
    can_assign_project: string[]; // List of project IDs or roles allowed to assign projects
    can_remove_project_assignment: string[]; // List of project IDs or roles allowed to remove project assignments
    can_assign_user: string[]; // List of user IDs or roles allowed to assign users
    can_remove_user_assignment: string[]; // List of user IDs or roles allowed to remove user assignments
}

// Define the shape of the context object
interface PermissionContextType {
    permissions: Permissions; // The permissions state
    loading: boolean; // Loading state for fetching permissions
    error: string | null; // Error state if permission fetch fails
    setPermissions: React.Dispatch<React.SetStateAction<Permissions>>; // Setter for updating permissions
}

// Create the context with proper typing
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
    children: ReactNode; // Children components that will consume the context
}

// PermissionProvider to manage permission states and provide them to children
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
    // State to manage permissions, loading status, and errors
    const [permissions, setPermissions] = useState<Permissions>({
        can_edit_users: [],
        can_delete_users: [],
        can_create_users: false,
        can_change_user_roles: false,
        can_create_projects: false,
        can_edit_projects: false,
        can_delete_projects: false,
        can_assign_project: [], // Initial empty array for project assignments
        can_remove_project_assignment: [], // Initial empty array for project assignment removals
        can_assign_user: [], // Initial empty array for user assignments
        can_remove_user_assignment: [], // Initial empty array for user assignment removals
    });
    const [loading, setLoading] = useState<boolean>(true); // Track if data is being fetched
    const [error, setError] = useState<string | null>(null); // Store any errors encountered during fetch

    // Fetch permissions from the server when the component mounts
    useEffect(() => {
        const fetchPermissions = async () => {
            const token = localStorage.getItem('token'); // Retrieve authentication token
            if (token) {
                try {
                    console.log('Fetching permissions...');
                    const response = await axiosInstance.get('permissions/'); // Request to get permissions
                    setPermissions(response.data); // Set permissions state with fetched data
                    console.log('Permissions fetched successfully:', response.data); // Log fetched permissions
                } catch (error) {
                    console.error('Error fetching permissions:', error); // Log any errors encountered
                    setError('Failed to load permissions. Please try again.'); // Set error message
                } finally {
                    setLoading(false); // Stop loading once fetch completes
                }
            } else {
                setLoading(false); // Stop loading if no token is found
            }
        };

        fetchPermissions(); // Call the function to fetch permissions
    }, []);

    // Provide the permissions context to children components
    return (
        <PermissionContext.Provider value={{ permissions, loading, error, setPermissions }}>
            {children}
        </PermissionContext.Provider>
    );
};

// Custom hook to easily access the PermissionContext in other components
export const usePermission = (): PermissionContextType => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermission must be used within a PermissionProvider'); // Error if used outside of provider
    }
    return context;
};

